import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Cinema, Movie, Showtime } from '../../models';
import { MovieService } from '../../services/movie.service';
import { ShowtimeService } from '../../services/showtime.service';
import { CinemaService } from '../../services/cinema.service';
import { ShowtimeResultRowComponent } from '../../components/showtime-result-row.component';
import { LocationFilterService } from '../../services/location-filter.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, ShowtimeResultRowComponent],
  template: `
    <section class="app-home">
      <div class="container">
        <div class="search-head">
          <div>
            <h1>What can I watch today?</h1>
            <p>{{ resultSummary() }}</p>
          </div>
          <a class="mini-link" routerLink="/cinemas">Cinemas</a>
        </div>

        <div class="glass search-panel" role="search">
          <input class="input search-input" [(ngModel)]="query" placeholder="Search movie, cinema, or town" (ngModelChange)="scheduleQueryUpdate()" (keyup.enter)="updateQueryParams()" />
          <div class="filter-line">
            <input class="input" [(ngModel)]="date" type="date" aria-label="Date" (ngModelChange)="updateQueryParams()" />
            <select [(ngModel)]="county" aria-label="County" (ngModelChange)="updateQueryParams()">
              <option value="">All counties</option>
              <option *ngFor="let county of counties">{{ county }}</option>
            </select>
          </div>
        </div>
      </div>
    </section>

    <section class="container results-page">
      <div *ngIf="error" class="error-state">{{ error }}</div>
      <div *ngIf="loading" class="compact-skeleton"></div>

      <ng-container *ngIf="!loading">
        <div class="result-layout">
          <section class="primary-results">
            <div class="compact-head">
              <h2>Showtimes</h2>
              <span>{{ showtimes.length }} found</span>
            </div>

            <div class="showtime-list" *ngIf="showtimes.length; else noTimes">
              <app-showtime-result-row *ngFor="let showtime of showtimes.slice(0, 18)" [showtime]="showtime" />
            </div>
            <ng-template #noTimes>
                <div class="empty-state compact-empty">No showtimes found. Try another date or county.</div>
            </ng-template>
          </section>

          <aside class="side-rail">
            <section>
              <div class="compact-head">
                <h2>Movies</h2>
                <a routerLink="/movies">All</a>
              </div>
              <div class="compact-movie-list" *ngIf="movies.length; else noMovies">
                <a class="compact-movie-row" *ngFor="let movie of movies.slice(0, 8)" [routerLink]="['/movies', movie.id]" [queryParams]="movieDetailParams()">
                  <span>{{ movie.title }}</span>
                  <small>{{ movie.language || (movie.runtimeMinutes ? movie.runtimeMinutes + ' min' : 'Times') }}</small>
                </a>
              </div>
              <ng-template #noMovies>
                <div class="empty-state compact-empty">No movies match these filters.</div>
              </ng-template>
            </section>

            <section>
              <div class="compact-head">
                <h2>Cinemas</h2>
                <a routerLink="/cinemas">All</a>
              </div>
              <div class="compact-cinema-list">
                <a class="compact-cinema-row" *ngFor="let cinema of cinemas.slice(0, 7)" [routerLink]="['/cinemas', cinema.id]" queryParamsHandling="preserve">
                  <span>{{ cinema.name }}</span>
                  <small>{{ cinema.city }}, {{ cinema.county }}</small>
                </a>
              </div>
            </section>
          </aside>
        </div>
      </ng-container>
    </section>
  `,
  styles: [
    `
      .app-home { padding: 18px 0 10px; position: sticky; top: 68px; z-index: 20; background: linear-gradient(180deg, var(--bg) 0%, rgba(7, 9, 20, 0.9) 72%, transparent 100%); backdrop-filter: blur(14px); }
      .search-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 14px; margin-bottom: 10px; }
      .search-head h1 { margin: 0 0 3px; font-size: clamp(24px, 4vw, 38px); line-height: 1.05; letter-spacing: 0; }
      .search-head p { margin: 0; color: var(--muted); font-size: 13px; font-weight: 750; }
      .mini-link { min-height: 36px; border: 1px solid var(--line); border-radius: 999px; padding: 8px 12px; color: var(--cyan); background: var(--control-bg); font-size: 13px; font-weight: 900; }
      .search-panel { display: grid; grid-template-columns: minmax(260px, 1fr) minmax(310px, .72fr); gap: 10px; padding: 10px; border-radius: 18px; box-shadow: 0 12px 34px rgba(0,0,0,.22); }
      .search-panel select { min-height: 46px; border: 1px solid var(--line); border-radius: 999px; padding: 0 14px; color: var(--text); background: var(--field-bg); }
      .search-input { font-weight: 800; }
      .filter-line { display: grid; grid-template-columns: minmax(142px, .85fr) minmax(150px, 1fr); gap: 10px; }
      .results-page { padding: 10px 0 64px; }
      .result-layout { display: grid; grid-template-columns: minmax(0, 1fr) 330px; gap: 18px; align-items: start; }
      .compact-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 8px 0 10px; }
      .compact-head h2 { margin: 0; font-size: 18px; line-height: 1.1; }
      .compact-head span, .compact-head a { color: var(--muted); font-size: 12px; font-weight: 900; }
      .showtime-list, .compact-movie-list, .compact-cinema-list { display: grid; gap: 8px; }
      .side-rail { display: grid; gap: 18px; position: sticky; top: 178px; }
      .compact-movie-row, .compact-cinema-row { min-height: 50px; padding: 10px 12px; border: 1px solid var(--line); border-radius: 14px; background: var(--surface-soft); display: grid; gap: 4px; }
      .compact-movie-row span, .compact-cinema-row span { color: var(--text); font-size: 14px; font-weight: 900; line-height: 1.15; overflow-wrap: anywhere; }
      .compact-movie-row small, .compact-cinema-row small { color: var(--muted); font-size: 12px; font-weight: 750; }
      .compact-empty { padding: 18px 14px; border-radius: 16px; }
      .compact-skeleton { min-height: 220px; border-radius: 18px; background: var(--skeleton); background-size: 220% 100%; animation: shimmer 1.25s linear infinite; }
      @media (max-width: 1024px) {
        .app-home { top: 64px; }
        .search-panel { grid-template-columns: 1fr; }
        .result-layout { grid-template-columns: 1fr; }
        .side-rail { position: static; grid-template-columns: 1fr 1fr; }
      }
      @media (max-width: 640px) {
        .app-home { padding: 10px 0 8px; top: 64px; }
        .search-head { align-items: flex-start; margin-bottom: 8px; }
        .search-head h1 { font-size: 22px; }
        .search-head p { font-size: 12px; }
        .mini-link { display: none; }
        .search-panel { padding: 8px; gap: 8px; border-radius: 16px; }
        .search-panel .input, .search-panel select { min-height: 44px; }
        .filter-line { grid-template-columns: 1fr 1fr; gap: 8px; }
        .results-page { padding-top: 8px; }
        .compact-head { margin: 6px 0 8px; }
        .side-rail { grid-template-columns: 1fr; gap: 12px; }
      }
      @media (max-width: 380px) {
        .filter-line { grid-template-columns: 1fr; }
      }
    `,
  ],
})
export class HomeComponent implements OnInit {
  query = '';
  county = '';
  date = new Date().toISOString().slice(0, 10);
  movies: Movie[] = [];
  showtimes: Showtime[] = [];
  cinemas: Cinema[] = [];
  private rawMovies: Movie[] = [];
  private rawShowtimes: Showtime[] = [];
  private rawCinemas: Cinema[] = [];
  loading = true;
  error = '';
  counties = ['Antrim', 'Armagh', 'Cavan', 'Clare', 'Cork', 'Derry', 'Donegal', 'Down', 'Dublin', 'Fermanagh', 'Galway', 'Kerry', 'Kildare', 'Laois', 'Limerick', 'Louth', 'Mayo', 'Meath', 'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary', 'Tyrone', 'Waterford', 'Westmeath', 'Wexford', 'Wicklow'];
  private queryTimer?: ReturnType<typeof setTimeout>;
  private locationScopeKey = 'all';

  constructor(
    private readonly movieService: MovieService,
    private readonly showtimeService: ShowtimeService,
    private readonly cinemaService: CinemaService,
    private readonly location: LocationFilterService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngOnInit() {
    this.location.state$.subscribe(() => {
      if (this.clearSearchFiltersForNewLocationScope()) return;
      this.applyLocationFilter();
      if (this.location.state.mode !== 'all') {
        this.load();
      }
    });
    this.route.queryParamMap.subscribe((params) => {
      this.query = params.get('query') ?? '';
      this.county = params.get('county') ?? '';
      this.date = params.get('date') ?? new Date().toISOString().slice(0, 10);
      this.load();
    });
  }

  resultSummary(): string {
    if (this.loading) return 'Loading live cinema times';
    if (this.location.isNearActive()) return `${this.showtimes.length} showtimes within ${this.location.state.radiusKm} km`;
    if (this.query || this.county) return `${this.showtimes.length} showtimes match your search`;
    return `${this.showtimes.length} live showtimes today`;
  }

  movieDetailParams(): Record<string, string> {
    return Object.fromEntries(
      [
        ['date', this.date],
        ['county', this.county],
      ].filter((entry): entry is [string, string] => Boolean(entry[1])),
    );
  }

  load() {
    this.loading = true;
    this.error = '';
    const county = this.location.state.mode !== 'all' ? '' : this.county;
    this.movieService.list({ query: this.query, county, date: this.date }).subscribe({
      next: (movies) => {
        this.rawMovies = movies;
        this.applyLocationFilter();
      },
      error: () => (this.error = 'Could not load movies. Check that the backend is running.'),
    });
    this.showtimeService.list({ movie: this.query, county, date: this.date }).subscribe({
      next: (showtimes) => {
        this.rawShowtimes = showtimes;
        this.applyLocationFilter();
        this.loading = false;
      },
      error: () => {
        this.error = 'Could not load showtimes. Check that the backend is running.';
        this.loading = false;
      },
    });
    this.cinemaService.list({ county }).subscribe((cinemas) => {
      this.rawCinemas = cinemas;
      this.applyLocationFilter();
    });
  }

  private applyLocationFilter() {
    this.showtimes = this.location.filterCinemas(this.rawShowtimes);
    this.cinemas = this.location.sortCinemasByDistance(this.location.filterCinemas(this.rawCinemas));
    if (this.location.state.mode !== 'all') {
      this.movies = this.uniqueMoviesFromShowtimes(this.showtimes);
      return;
    }
    this.movies = this.rawMovies;
  }

  private uniqueMoviesFromShowtimes(showtimes: Showtime[]): Movie[] {
    const map = new Map<string, Movie>();
    showtimes.forEach((showtime) => {
      if (showtime.movie) map.set(showtime.movie.id, showtime.movie);
    });
    return Array.from(map.values()).sort((a, b) => a.title.localeCompare(b.title));
  }

  private clearSearchFiltersForNewLocationScope(): boolean {
    const state = this.location.state;
    const scopeKey = state.mode === 'manual' ? `manual:${state.label ?? ''}` : state.mode;
    const changed = scopeKey !== this.locationScopeKey;
    this.locationScopeKey = scopeKey;

    if (!changed || state.mode === 'all') return false;

    const hasLocationConflictingFilters = Boolean(this.query || this.county);
    if (!hasLocationConflictingFilters) return false;

    this.query = '';
    this.county = '';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        query: null,
        county: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    return true;
  }

  scheduleQueryUpdate() {
    if (this.queryTimer) clearTimeout(this.queryTimer);
    this.queryTimer = setTimeout(() => this.updateQueryParams(), 350);
  }
  updateQueryParams() {
    if (this.queryTimer) clearTimeout(this.queryTimer);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        query: this.query.trim() || null,
        county: this.county || null,
        date: this.date || null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  initials(title: string): string {
    return title.split(/\s+/).slice(0, 2).map((word) => word[0]).join('').toUpperCase();
  }
}
